import React, { ReactNode, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import './Dictpicker.scss';
// Триггер select-режима собирается из классов Select — пиксельный паритет с атомом
// Select (рамка-тень, размеры, шеврон с поворотом); scss подключаем самодостаточно.
// Атом живёт в @srm/ui, его стили приходят с модулем — порядок подключения прежний.
import '@srm/ui/select';
import { Popover } from '@mantine/core';
import { assignRef } from '@mantine/hooks';
import { MdAdd, MdMenu, MdOutlineExpandLess, MdOutlineExpandMore } from 'react-icons/md';
import Input from '@srm/ui/input';
import clsx from 'clsx';
import DictpickerModal from './DictpickerModal/DictpickerModal';
import { IDictionaryData } from '@models/dictionary/IDictionaryData';
import {
    IAddDictForm,
    IDictFilter,
    IDictSource,
    IExternalSearchDict,
    IGridModalAttributes,
    IGridUserScripts,
    IJoinedDictionary,
    ISetValues,
} from '@models/Forms/IForms';
import DictpickerCombobox from './DictpickerCombobox/DictpickerCombobox';
import { usePickerDropdownPlacement } from '@atoms/PickerCombobox/usePickerDropdownPlacement';
import { loadDictScheme, peekDictScheme } from './loadDictScheme';
import { parseDictionarySelectableLevels } from './dictionarySelectableLevels';
import DictChipItem from '@molecules/formbuilder/controls/Dict/DictChipItem/DictChipItem';
import Button from '@srm/ui/button';
import { IDictpickerRefActions } from './IDictpickerRefActions';
import { EMPTY_DISPLAY_PLACEHOLDER } from '@srm/ui/theme/forms';
import { FieldA11yContext } from '@srm/ui/context/FieldA11yContext';
import { useFieldA11y } from '@srm/ui/hooks/useFieldA11y';
import { fieldFocusTarget, type IFieldFocusTarget } from '@srm/ui/lib/fieldFocusTarget';

export interface IDictpickerProps {
    /**
     * Фокус поля формы (`setFocus`, ошибка валидации): поле-триггер, у фишек и своей кнопки (`children`) — объект с
     * `focus()` («Добавить» или кнопка владельца)
     */
    ref?: React.Ref<HTMLInputElement | IFieldFocusTarget>;
    /** Имя поля */
    name?: string;
    /** Имя справочника */
    dictionaryName: string;
    /** Псевдо-справочник */
    isFormData: boolean;
    isMultiple: boolean;
    /**
     * При isMultiple=true — рекурсивное выделение узлов в TreeMode
     */
    recursive?: boolean;
    isSelectMode: boolean;
    getExternalDataSource?: () => IDictionaryData[];
    getFormValuesAsync: () => Promise<string>;
    getFiltersAsync: () => Promise<IDictFilter>;
    onValidate?: (data: IDictionaryData[]) => Promise<boolean>;
    /** зависимые поля*/
    setValues?: ISetValues;
    /** колонки для грида */
    gridAttribute?: IGridModalAttributes;
    /** Уровень загрузки справочника */
    startLevel?: number;
    /** Текст Placeholder */
    placeholder?: string;
    /** Значение по умолчанию */
    defaultValue?: any;
    /** отображаемое значение */
    displayValue: string;
    /** Чистое значение поля */
    rawVal?: any;
    /** Скрыть поле */
    disabled?: boolean;
    /** Только для просмотра */
    readOnly?: boolean;
    /** Функция изменения значения */
    onChange?: (value: IDictionaryData[]) => Promise<void>;
    /** Функция отмены */
    onCancel?: () => void;
    /** Кастомная кнопка */
    children?: ReactNode | ReactNode[];
    /** Переводит инпут в невалидный статус */
    invalid?: boolean;
    /** Реф для пробрасывания действий по кастомным кнопкам */
    controlRef?: React.Ref<IDictpickerRefActions>;
    /**
     *  Добавлять фокус при выборе дат
     * @default false
     *  */
    isFocusBorder?: boolean;
    /** Компактный вид внутри ячейки грида @default false */
    insideGrid?: boolean;
    autoFocus?: boolean;
    /** Доступные уровни: first, last, 4, 4-6, first-4, 4-last (границы включительно). */
    selectableLevels?: string;
    /** Отображаемые уровни */
    visibleLevels?: string;
    /** Ключ кэша предикатов */
    predicatesCache?: string;
    externalSearch?: IExternalSearchDict;
    addForm?: IAddDictForm;
    /**
     * Режим загрузки справочника
     * */
    loadMode?: string;

    docId?: string;

    modalTitle?: string;

    joinedDictionaries?: IJoinedDictionary[];

    /**
     * Дополнительная настройка источника данных (выбор данных из реквизитов-таблиц и т.д.)
     */
    source?: IDictSource;
    /**
     * Флаг обработки операций фильтрации, поиска и т.п. на стороне клиента
     */
    useClientSideDataProcessing?: boolean;

    /** Отображение в виде чипсов */
    chipsView?: boolean;

    /*
     * Пользовательские скрипты
     */
    userScripts?: IGridUserScripts;

    /**
     * Максимальное количество отображаемых чипсов
     * @default 20
     */
    maxVisibleChips?: number;

    testid?: string;
    wordWrapEnabled?: boolean;
    showActionsBar?: boolean;
    /** Подписи пустых полей формы, от которых зависит список, — для пустого выпадающего списка. */
    getEmptyDependencies?: () => string[];
}

// FIXME: открытие окна с клавиатуры (в режиме списка оно есть)
const Dictpicker: React.FC<IDictpickerProps> = ({
    ref,
    name = 'dictpicker',
    placeholder = 'Выберите значение',
    defaultValue,
    displayValue,
    rawVal,
    gridAttribute,
    dictionaryName,
    invalid = false,
    disabled = false,
    readOnly = false,
    onChange,
    onCancel,
    onValidate,
    children,
    isFormData,
    isMultiple,
    recursive,
    getFormValuesAsync,
    getFiltersAsync,
    controlRef,
    getExternalDataSource,
    isFocusBorder = false,
    insideGrid = false,
    autoFocus,
    selectableLevels = '',
    visibleLevels = '',
    predicatesCache = '',
    loadMode = '',
    useClientSideDataProcessing = false,
    isSelectMode,
    docId,
    externalSearch,
    addForm,
    modalTitle,
    joinedDictionaries,
    source,
    chipsView = false,
    userScripts,
    maxVisibleChips = 20,
    testid,
    wordWrapEnabled,
    showActionsBar,
    getEmptyDependencies,
}: IDictpickerProps) => {
    // Дерево выбирается только в окне: список справочника отдаёт одни корни, вложенные уровни и правила
    // selectableLevels/visibleLevels в нём не выбрать. Только корни (`first`) список выбирает и у дерева.
    const levels = parseDictionarySelectableLevels(selectableLevels);
    const rootsOnly = levels.mode === 'first' || (levels.mode === 'range' && levels.max === 1);
    const checksTree = isSelectMode && !isFormData && !readOnly && !disabled && !rootsOnly;
    // undefined — схема ещё грузится; загруженная раньше берётся сразу, без мелькания списка
    const [isTree, setIsTree] = useState<boolean | undefined>(() =>
        checksTree ? peekDictScheme(dictionaryName, false)?.hasNestedItems : false,
    );
    useEffect(() => {
        if (!checksTree) {
            setIsTree(false);
            return;
        }
        let cancelled = false;
        loadDictScheme(dictionaryName, false).then(
            (scheme) => !cancelled && setIsTree(!!scheme.hasNestedItems),
            // схема не загрузилась — остаётся список, как без проверки
            () => !cancelled && setIsTree(false),
        );
        return () => {
            cancelled = true;
        };
    }, [checksTree, dictionaryName]);

    // Режим «выпадающий список»: не read-only и не дерево — общий ключ для триггера, поповера и модалки.
    const isSelectDropdown = isSelectMode && !readOnly && isTree !== true;

    // read-only-проп/disabled = display-only (не интерактивный триггер): приглашающий
    // плейсхолдер лжёт, пустое поле показывает прочерк-маркер вместо «Выберите значение».
    const resolvedPlaceholder = readOnly || disabled ? EMPTY_DISPLAY_PLACEHOLDER : placeholder;
    // поле-триггер списка — своё <input>, подпись и сообщение группы формы (FormGroup) берёт само
    const fieldA11y = useFieldA11y({});

    useImperativeHandle(controlRef, () => ({
        reset: () => {},
        setSelected: () => {},
        unSelect: () => {},
        clearSelection: () => {},
    }));

    // -------------------------------------------------------------------------------------------------------------------

    const dictpickerRef = useRef<HTMLDivElement>(null);
    const triggerInput = useRef<HTMLInputElement | null>(null);
    const inputRef = useCallback(
        (input: HTMLInputElement | null) => {
            triggerInput.current = input;
            assignRef(ref, input);
        },
        [ref],
    );
    // фишки и своя кнопка — без поля ввода: фокус формы — на первую кнопку внутри («Добавить», кнопка владельца)
    const ownTrigger = chipsView || !!children;
    const triggerRef = useCallback(
        (trigger: HTMLDivElement | null) => {
            if (ownTrigger) assignRef(ref, trigger && fieldFocusTarget(trigger));
        },
        [ref, ownTrigger],
    );

    const [showDictionary, toggleDictionary] = useState<boolean>(false);
    // select-режим: символ, напечатанный на закрытом поле, и выход из списка в модалку
    const [initialQuery, setInitialQuery] = useState<string>();
    const [showFullList, setShowFullList] = useState(false);
    const [showAllChips, setShowAllChips] = useState<boolean>(false);
    const dropdownPlacement = usePickerDropdownPlacement(dictpickerRef, showDictionary && isSelectDropdown);

    // пока не ясно, дерево ли это, ячейка таблицы не раскрывает список, который тут же сменится окном
    const pickerKnown = isTree !== undefined;
    useEffect(() => {
        if (autoFocus && !readOnly && !chipsView && pickerKnown) {
            toggleDictionary(true);
        }
    }, [autoFocus, chipsView, readOnly, pickerKnown]);

    const onCloseModal = useCallback(() => {
        toggleDictionary(false);
        setShowFullList(false);
        setInitialQuery(undefined);
        onCancel && onCancel();
    }, [onCancel]);

    const openFullList = useCallback(() => {
        toggleDictionary(false);
        setShowFullList(true);
    }, []);

    // модалка открыта из списка, которого уже нет, — фокус возвращаем полю
    const closeFullList = useCallback(() => {
        onCloseModal();
        requestAnimationFrame(() => triggerInput.current?.focus({ preventScroll: true }));
    }, [onCloseModal]);

    // -------------------------------------------------------------------------------------------------------------------

    const [inputValue, setInputValue] = useState<string[]>();

    const values = useMemo(() => {
        return typeof rawVal === 'string' && rawVal
            ? rawVal.split('|').sort((a, b) => {
                  const textA = a.toUpperCase();
                  const textB = b.toUpperCase();
                  return textA < textB ? -1 : textA > textB ? 1 : 0;
              })
            : [];
    }, [rawVal]);

    useEffect(() => {
        if (!defaultValue) {
            setInputValue(undefined);
        } else {
            if (source?.table) {
                const keys = (defaultValue as any[])?.map((row: any) => row[source.key]);
                if (keys) {
                    setInputValue(keys);
                }
            } else {
                if (defaultValue.toString().indexOf('|') > 0) {
                    setInputValue(defaultValue.toString().split('|'));
                } else {
                    const arr = [] as string[];
                    arr.push(defaultValue);
                    setInputValue(arr);
                }
            }
        }
    }, [defaultValue]);

    const toggleDict = () => {
        // клик по раскрытому списку — отмена, как Escape: редактор ячейки таблицы закрывается вместе с ним
        if (isSelectDropdown && showDictionary) {
            onCloseModal();
            return;
        }
        // клик по полю — без напечатанного раньше символа
        setInitialQuery(undefined);
        toggleDictionary((prev) => !prev);
    };

    // Determine which items to display based on showAllChips state
    const displayedValues = useMemo(() => {
        if (showAllChips || !values || values.length <= maxVisibleChips) {
            return values;
        }
        return values.slice(0, maxVisibleChips);
    }, [values, showAllChips, maxVisibleChips]);

    // Determine if we need to show the "Show More" button
    const hasMoreChips = useMemo(() => {
        return values && values.length > maxVisibleChips;
    }, [values, maxVisibleChips]);

    const toggleShowAllChips = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setShowAllChips((prev) => !prev);
    }, []);

    // Триггер общий для трёх режимов (select/модалка/чипы): в select-режиме он же —
    // Popover.Target, поэтому вынесен в переменную.
    const triggerNode = (
        <div
            ref={triggerRef}
            className={clsx('rf-dictpicker__input-wrapper', {
                'rf-dictpicker__input-wrapper--disabled': disabled,
                'rf-dictpicker__input-wrapper--readonly': readOnly,
            })}
            onClick={!readOnly && !chipsView ? toggleDict : undefined}
            // Escape закрывает дропдаун при фокусе в поле (паритет Select; Mantine
            // Popover ловит Escape только на target, а легаси-useDismiss — на документе);
            // Enter/Space/стрелки открывают с клавиатуры, напечатанный символ — с поиском; дальше фокус
            // уходит в список (DictpickerCombobox ставит его в строку поиска или в сам список).
            onKeyDown={
                isSelectDropdown
                    ? (e: React.KeyboardEvent) => {
                          if (e.key === 'Escape' && showDictionary) {
                              e.stopPropagation();
                              onCloseModal();
                              return;
                          }
                          if (
                              !showDictionary &&
                              (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp')
                          ) {
                              e.preventDefault();
                              toggleDictionary(true);
                              return;
                          }
                          // печать на закрытом поле начинает поиск в списке
                          if (!showDictionary && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
                              e.preventDefault();
                              setInitialQuery(e.key);
                              toggleDictionary(true);
                          }
                      }
                    : undefined
            }
        >
            {children ||
                (chipsView ? (
                    // группа — само поле: подпись группы формы — её имя, сообщение под полем — у «Добавить»
                    <div
                        className="dict-chips-container"
                        role={fieldA11y['aria-labelledby'] ? 'group' : undefined}
                        aria-labelledby={fieldA11y['aria-labelledby']}
                    >
                        <div className="dict-chips-add">
                            <Button
                                size="sm"
                                variant={'round'}
                                tone="neutral"
                                onClick={(e: any) => {
                                    e.stopPropagation();
                                    toggleDict();
                                }}
                                className={readOnly ? 'dict-chips-add--disabled' : undefined}
                                disabled={readOnly}
                                startAdornment={<MdAdd size="24" />}
                                aria-label="Добавить"
                                aria-describedby={fieldA11y['aria-describedby']}
                            />
                        </div>
                        <div className="dict-chips-items">
                            {displayedValues &&
                                displayedValues.length > 0 &&
                                displayedValues.map((item: string, index: number) => (
                                    <DictChipItem
                                        key={index}
                                        item={item}
                                        showRemoveIcon={!readOnly}
                                        onClick={
                                            !readOnly
                                                ? () => {
                                                      toggleDict();
                                                  }
                                                : undefined
                                        }
                                        onRemove={async () => {
                                            onChange &&
                                                (await onChange(
                                                    values
                                                        .filter((x) => x != item)
                                                        .map((val) => {
                                                            return { code: val } as IDictionaryData;
                                                        }),
                                                ));
                                        }}
                                    />
                                ))}
                            {values === undefined || values.length === 0 ? (
                                <div className="dict-chips-placeholder">{resolvedPlaceholder}</div>
                            ) : hasMoreChips ? (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={toggleShowAllChips}
                                    className="dict-chips-show-more"
                                    startAdornment={
                                        showAllChips ? (
                                            <MdOutlineExpandLess size="20" />
                                        ) : (
                                            <MdOutlineExpandMore size="20" />
                                        )
                                    }
                                >
                                    {showAllChips ? 'Скрыть' : `Ещё ${values.length - maxVisibleChips}`}
                                </Button>
                            ) : null}
                        </div>
                    </div>
                ) : isSelectDropdown ? (
                    // Select-режим: триггер собран из классов атома Select —
                    // те же рамка-тень, размеры (input 40px, паддинги 16/48) и
                    // шеврон с поворотом при открытом дропдауне.
                    <div
                        className={clsx('rf-select__wrapper', {
                            'rf-select__wrapper--invalid': invalid,
                            'rf-select__wrapper--disabled': disabled,
                            'rf-select__wrapper--open': showDictionary,
                        })}
                    >
                        <input
                            ref={inputRef}
                            className="rf-select__input"
                            data-testid="rf-dictpicker-select-trigger"
                            {...fieldA11y}
                            aria-invalid={invalid || undefined}
                            name={name}
                            value={displayValue ?? ''}
                            placeholder={resolvedPlaceholder}
                            disabled={disabled}
                            readOnly
                            // autoFocus раскрывает список эффектом при монтировании —
                            // редактор ячейки таблицы открывает
                            // справочник одним кликом по ячейке
                            autoFocus={autoFocus}
                            autoComplete="off"
                            onChange={() => {}}
                        />
                        <div
                            className={clsx('rf-select__button', showDictionary && 'rf-select__button--rotate')}
                            data-testid="rf-select__chevron"
                            aria-label={showDictionary ? 'Скрыть меню' : 'Раскрыть меню'}
                        >
                            <MdOutlineExpandMore size="20" />
                        </div>
                    </div>
                ) : (
                    <Input
                        ref={inputRef}
                        name={name}
                        value={displayValue}
                        placeholder={resolvedPlaceholder}
                        disabled={disabled}
                        readOnly={true}
                        // Интерактивный триггер: role=combobox — вид обычного Input из темы.
                        role={readOnly ? undefined : 'combobox'}
                        invalid={invalid}
                        autoFocus={autoFocus}
                        rightSection={
                            !readOnly && !disabled ? (
                                <div className={'rf-dictpicker__calendar-chevron'}>
                                    <MdMenu size={18} />
                                </div>
                            ) : (
                                <></>
                            )
                        }
                    />
                ))}
        </div>
    );

    return (
        <div
            className={clsx(
                !insideGrid && 'rf-dictpicker',
                insideGrid && 'rf-dictpicker-ingrid',
                isFocusBorder && 'rf-dictpicker__focus-border',
                disabled && 'rf-dictpicker--disabled',
            )}
            ref={dictpickerRef}
            data-testid={testid ? `dictpicker-${testid}` : undefined}
        >
            {isSelectDropdown ? (
                // Select-режим: дропдаун — Mantine Popover (портал, позиционирование,
                // дисмисс из коробки; duration 0 — Transition монтирует через rAF).
                // Ширина — по полю, длинные подписи переносятся; в узкой колонке не уже
                // 400 px: рядом с колонкой кода названию остаётся место. fixed: список у нижнего края окна не удлиняет
                // страницу — иначе появлялась прокрутка страницы и окно становилось уже.
                <Popover
                    opened={showDictionary}
                    // Дисмисс поповера (клик вне, Escape) — это отмена выбора, а не
                    // только закрытие списка: редактор ячейки таблицы живёт ровно
                    // столько, сколько открыт его пикер, и без onCancel ячейка
                    // оставалась «раскрытой» после того, как список уже закрылся.
                    onChange={(opened) => (opened ? toggleDictionary(true) : onCloseModal())}
                    withinPortal
                    keepMounted={false}
                    transitionProps={{ duration: 0 }}
                    zIndex={1000}
                    {...dropdownPlacement}
                    width="target"
                    floatingStrategy="fixed"
                    styles={{ dropdown: { minWidth: 'min(400px, calc(100vw - 32px))' } }}
                    returnFocus
                    // Escape в списке обрабатывает DictpickerCombobox: второй onCancel закрыл бы редактор ячейки дважды
                    closeOnEscape={false}
                    offset={8}
                >
                    <Popover.Target>{triggerNode}</Popover.Target>
                    <Popover.Dropdown className="rf-select__dropdown rf-dropdown__content" data-testid="rf-dropdown">
                        <FieldA11yContext value={null}>
                            <DictpickerCombobox
                                selected={inputValue!}
                                docId={docId}
                                dictName={dictionaryName}
                                modalTitle={modalTitle}
                                isFormData={isFormData}
                                getExternalDataSource={getExternalDataSource}
                                isMultiple={isMultiple}
                                selectableLevels={selectableLevels}
                                visibleLevels={visibleLevels}
                                predicatesCache={predicatesCache}
                                loadMode={loadMode}
                                useClientSideDataProcessing={useClientSideDataProcessing}
                                getFormValuesAsync={getFormValuesAsync}
                                getFiltersAsync={getFiltersAsync}
                                gridAttribute={gridAttribute}
                                joinedDictionaries={joinedDictionaries}
                                initialQuery={initialQuery}
                                getEmptyDependencies={getEmptyDependencies}
                                onSubmit={onChange!}
                                onClose={onCloseModal}
                                onOpenFullList={openFullList}
                            />
                        </FieldA11yContext>
                    </Popover.Dropdown>
                </Popover>
            ) : (
                triggerNode
            )}
            {((showDictionary && !isSelectDropdown) || showFullList) && (
                // поля окна выбора — не поле группы формы: её подпись и ошибка относятся к триггеру
                <FieldA11yContext value={null}>
                    <DictpickerModal
                        selected={inputValue!}
                        docId={docId}
                        dictName={dictionaryName}
                        modalTitle={modalTitle}
                        isFormData={isFormData}
                        getExternalDataSource={getExternalDataSource}
                        isMultiple={isMultiple}
                        recursive={recursive}
                        selectableLevels={selectableLevels}
                        visibleLevels={visibleLevels}
                        predicatesCache={predicatesCache}
                        externalSearch={externalSearch}
                        addForm={addForm}
                        loadMode={loadMode}
                        useClientSideDataProcessing={useClientSideDataProcessing}
                        getFormValuesAsync={getFormValuesAsync}
                        getFiltersAsync={getFiltersAsync}
                        gridAttribute={gridAttribute}
                        onSubmit={onChange!}
                        onClose={showFullList ? closeFullList : onCloseModal}
                        joinedDictionaries={joinedDictionaries}
                        onValidate={onValidate}
                        userScripts={userScripts}
                        wordWrapEnabled={wordWrapEnabled}
                        showActionsBar={showActionsBar}
                    />
                </FieldA11yContext>
            )}
        </div>
    );
};

export default Dictpicker;
